import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { useWizard } from 'ui-components/components/wizard/context';

import { useModal } from '../../../../../../hooks/use-modal';
import { useWalletRegistrationErrorContext } from '../../../contexts/wallet-registration-error-context';
import { postAddressValidation } from '../../../../../utilities/api';
import { useWalletRegistrationFormContext } from '../../../contexts/wallet-registration-form-context';
import { AddressValidationModal } from '../../../../address-validation-modal';
import style from '../style.module.less';
import { messages } from '../../../../../messages';
import { useWalletRegistrationModalContext } from '../../../contexts/wallet-registration-modal-context';
import PropTypes from 'prop-types';
import { ProgressButton } from './progress-button';
import { PaymentMethod } from '@shipstation/financial-services-ui-components';

const LINE_1 = 'line1';
const LINE_2 = 'line2';
const LINE_3 = 'line3';
const CITY = 'city';
const STATE = 'state';
const POSTAL_CODE = 'postalCode';
const COUNTRY_CODE = 'countryCode';
const COMPANY = 'company';

const fieldsToValidate = [LINE_1, LINE_2, LINE_3, CITY, STATE, POSTAL_CODE, COUNTRY_CODE, COMPANY];

export const addressObjectEqualityEvaluator = (inputAddress, matchedAddress) => {
  if (!matchedAddress) {
    return false;
  }
  return fieldsToValidate.every((key) => inputAddress[key]?.toLowerCase() === matchedAddress[key]?.toLowerCase());
};

export const SubmitPaymentInfoButton = ({ disabled = false }) => {
  const { stepForward, canProgress } = useWizard();
  const { setShowAllErrors } = useWalletRegistrationErrorContext();
  const { shouldShowAdyenDropInComponent, paymentType } = useWalletRegistrationModalContext();
  const { billingAddress, setIsVerifiedAddress, hasSubmittedPayment, setHasSubmittedPayment, isPaymentComplete, performValidateForm } =
    useWalletRegistrationFormContext();
  const [isFinalizingPayment, setIsFinalizingPayment] = useState(false);

  const [validationResponse, setValidationResponse] = useState(null);
  const { close: closeAddressValidationModal, isOpen: isAddressValidationModalOpen, open: openAddressValidationModal } = useModal();

  useEffect(() => {
    if (canProgress) {
      setShowAllErrors(false);
    }
  }, [canProgress, setShowAllErrors]);

  useEffect(() => {
    if (isPaymentComplete && shouldShowAdyenDropInComponent) {
      setHasSubmittedPayment(false);
      setIsFinalizingPayment(true);

      // waiting two seconds for adyen drop in component to play the authorized animation before proceeding.
      const timer = setTimeout(() => {
        setIsFinalizingPayment(false);
        stepForward();
      }, 2000);

      return () => clearTimeout(timer);
    }

    // clear state so that the button can be clicked again in case of an error downstream
    if (!isPaymentComplete && shouldShowAdyenDropInComponent) {
      setIsFinalizingPayment(false);
      setHasSubmittedPayment(false);
    }
  }, [isPaymentComplete, stepForward, setHasSubmittedPayment, shouldShowAdyenDropInComponent]);

  const isAdyenActiveAndProcessing = shouldShowAdyenDropInComponent && (hasSubmittedPayment || isFinalizingPayment);
  const isDisabled = disabled || isAdyenActiveAndProcessing;

  const validateAddress = useCallback(async () => {
    try {
      const resp = await postAddressValidation(billingAddress);
      return resp;
    } catch (errorResponse) {
      const errors = errorResponse?.response?.data?.Errors || [];
      return { errors };
    }
  }, [billingAddress]);

  const advanceToNextStep = useCallback(() => {
    closeAddressValidationModal();

    if (!canProgress) {
      setShowAllErrors(true);
    } else if (isPaymentComplete) {
      stepForward();
    } else if (canProgress && shouldShowAdyenDropInComponent) {
      setHasSubmittedPayment(true);
    } else if (canProgress) {
      stepForward();
    } else {
      setShowAllErrors(true);
    }
  }, [
    canProgress,
    closeAddressValidationModal,
    isPaymentComplete,
    setHasSubmittedPayment,
    setShowAllErrors,
    shouldShowAdyenDropInComponent,
    stepForward,
  ]);

  const actionClicked = useCallback(async () => {
    if (!canProgress) {
      setShowAllErrors(true);
      performValidateForm?.();
      return;
    }
    const validationResp = await validateAddress();
    const { originalAddress, matchedAddress } = validationResp;
    const areAddressesEqual = addressObjectEqualityEvaluator(originalAddress, matchedAddress);
    if (!areAddressesEqual && canProgress) {
      setValidationResponse(validationResp);
      setIsVerifiedAddress(true);
      openAddressValidationModal();
      return;
    }
    advanceToNextStep();
  }, [
    advanceToNextStep,
    canProgress,
    openAddressValidationModal,
    performValidateForm,
    setIsVerifiedAddress,
    setShowAllErrors,
    validateAddress,
  ]);

  return (
    <>
      <Button classname={style.button} disabled={isDisabled} onClick={actionClicked} theme='primary'>
        <FormattedMessage
          {...(paymentType === (PaymentMethod.PayByBankUS || PaymentMethod.PayByBankEU)
            ? messages.continueWithPayByBank
            : messages.continueSetup)}
        />
      </Button>
      {isAddressValidationModalOpen && (
        <AddressValidationModal
          addressResponse={validationResponse}
          advanceToNextStep={advanceToNextStep}
          billingAddress={billingAddress}
          onClose={closeAddressValidationModal}
        />
      )}
    </>
  );
};

ProgressButton.propTypes = {
  disabled: PropTypes.bool,
};
